import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';

import CalendarInput from '../../../component/input/CalendarInput';
import InputSwitch from '../../../component/input/InputSwitch';

import { ReactComponent as WarningTriangleIcon } from '../../../assets/images/warning-triangle-icon.svg';
import { ReactComponent as QuestionMarkIcon } from '../../../assets/images/questionmark-icon.svg';

import { getNextBusinessDay } from '../../../utils/date/DateUtils';

import './ChangedConfigurationDialog.scss';

const ChangedConfigurationDialog = ({
    visible,
    onHide,
    onSave,
    assignedDevicesCount = 0,
    remotePriceUpdate = false,
}) => {
    const { t } = useTranslation();

    const [step, setStep] = useState(1);
    const [effectiveDate, setEffectiveDate] = useState(null);
    const [applyImmediately, setApplyImmediately] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!visible) return;

        setStep(1);
        setEffectiveDate(null);
        setApplyImmediately(false);
        setIsSaving(false);
    }, [visible]);

    const normalizeDate = date => {
        if (!date || !(date instanceof Date)) {
            return date;
        }

        if (!remotePriceUpdate) {
            return new Date(
                date.getFullYear(),
                date.getMonth(),
                date.getDate(),
                0,
                0,
                0,
                0,
            );
        }

        return date;
    };

    const handleCancel = () => {
        setStep(1);
        setEffectiveDate(null);
        setApplyImmediately(false);
        setIsSaving(false);
        onHide?.();
    };

    const savePayload = async payload => {
        setIsSaving(true);

        try {
            await onSave?.(payload);
        } finally {
            setIsSaving(false);
        }
    };

    const handleImmediateSave = async () => {
        await savePayload({
            effectiveDate: new Date(),
            applyImmediately: true,
        });
    };

    const handleNextBusinessDaySave = async () => {
        const nextBusinessDay = getNextBusinessDay(new Date());

        await savePayload({
            effectiveDate: normalizeDate(nextBusinessDay),
            applyImmediately: false,
        });
    };

    const handleSelectedDaySave = async () => {
        if (!effectiveDate) return;

        await savePayload({
            effectiveDate: normalizeDate(effectiveDate),
            applyImmediately: false,
        });
    };

    const renderFooter = () => {
        if (step === 1 && applyImmediately) {
            return (
                <div className="flex w-full gap-3">
                    <Button
                        label={t('common.save', 'Save')}
                        onClick={() => void handleImmediateSave()}
                        disabled={isSaving}
                        loading={isSaving}
                        className="flex-1"
                    />

                    <Button
                        label={t('common.cancel', 'Cancel')}
                        onClick={handleCancel}
                        disabled={isSaving}
                        className="flex-1 p-button-secondary"
                    />
                </div>
            );
        }

        if (step === 1) {
            return (
                <div className="flex w-full gap-3">
                    <Button
                        label={t(
                            'planogram.changedConfiguration.nextBusinessDay',
                            'Next business day',
                        )}
                        onClick={() => void handleNextBusinessDaySave()}
                        disabled={isSaving}
                        loading={isSaving}
                        className="flex-1"
                    />

                    <Button
                        label={
                            remotePriceUpdate
                                ? t(
                                    'planogram.changedConfiguration.selectedDayAndTime',
                                    'Selected day and time',
                                )
                                : t(
                                    'planogram.changedConfiguration.selectedDay',
                                    'Selected day',
                                )
                        }
                        onClick={() => setStep(2)}
                        disabled={isSaving}
                        className="flex-1 p-button-secondary"
                    />
                </div>
            );
        }

        return (
            <div className="flex w-full gap-3">
                <Button
                    label={t('common.save', 'Save')}
                    onClick={() => void handleSelectedDaySave()}
                    disabled={isSaving || !effectiveDate}
                    loading={isSaving}
                    className="flex-1"
                />

                <Button
                    label={t('common.cancel', 'Cancel')}
                    onClick={handleCancel}
                    disabled={isSaving}
                    className="flex-1 p-button-secondary"
                />
            </div>
        );
    };

    return (
        <Dialog
            visible={visible}
            onHide={handleCancel}
            header={t(
                'planogram.changedConfiguration.title',
                'Changed configuration',
            )}
            className="changed-configuration-dialog"
            closable
            closeOnEscape
            modal
            draggable={false}
            resizable={false}
            focusOnShow
            footer={renderFooter()}
        >
            {step === 1 && (
                <div className="flex flex-column gap-3">
                    <div className="warning-section">
                        <WarningTriangleIcon className="warning-icon" />

                        <span className="warning-text">
                            {t(
                                'planogram.changedConfiguration.warning',
                                'Assigned devices',
                            )}
                        </span>
                    </div>

                    <div className="description">
                        {t(
                            'planogram.changedConfiguration.scheduleMessage',
                            'This planogram is assigned to a device. When should the changes be applied?',
                        )}
                    </div>

                    <div className="device-info-section">
                        <label className="field-label mb-0">
                            {t(
                                'planogram.changedConfiguration.devicesLabel',
                                'DEVICES',
                            )}
                        </label>

                        <div className="assigned-count-badge">
                            <span className="assigned-label">
                                {t(
                                    'planogram.changedConfiguration.assignedLabel',
                                    'Assigned:',
                                )}
                            </span>

                            <span className="assigned-count">
                                {assignedDevicesCount}
                            </span>
                        </div>
                    </div>

                    <div className="apply-immediately-row">
                        <InputSwitch
                            id="changed-configuration-apply-immediately"
                            label={t(
                                'planogram.changedConfiguration.applyImmediately',
                                'Apply immediately',
                            )}
                            checked={applyImmediately}
                            onChange={event => setApplyImmediately(Boolean(event.value))}
                            className="w-auto"
                        />

                    </div>
                </div>
            )}

            {step === 2 && (
                <div className="flex flex-column gap-3">
                    <div className="description">
                        {t(
                            'planogram.changedConfiguration.selectedDayDescription',
                            'Please select the date when the planogram changes should be applied to assigned devices.',
                        )}
                    </div>

                    <div className="device-info-section">
                        <label className="field-label mb-0">
                            {t(
                                'planogram.changedConfiguration.devicesLabel',
                                'DEVICES',
                            )}
                        </label>

                        <div className="assigned-count-badge">
                            <span className="assigned-label">
                                {t(
                                    'planogram.changedConfiguration.assignedLabel',
                                    'Assigned:',
                                )}
                            </span>

                            <span className="assigned-count">
                                {assignedDevicesCount}
                            </span>
                        </div>
                    </div>

                    <div className="flex flex-column gap-2 w-full">
                        <label className="field-label">
                            {t(
                                'planogram.changedConfiguration.effectiveDate',
                                'DATE',
                            )}
                        </label>

                        <CalendarInput
                            id="changed-configuration-effective-date"
                            value={effectiveDate}
                            onChange={event => setEffectiveDate(event.value ?? null)}
                            dateFormat="yy-mm-dd"
                            showTime={remotePriceUpdate}
                            hourFormat="24"
                            className="planogram-calendar"
                            inputClassName="planogram-calendar__input"
                            panelClassName="changed-configuration-calendar-panel"
                            showLabel={false}
                            iconPosition="left"
                            minDate={new Date()}
                            placeholder={t('common.select', 'Select')}
                        />
                    </div>
                </div>
            )}
        </Dialog>
    );
};

export default ChangedConfigurationDialog;